import { createContext, useState, useContext, ReactNode, useEffect } from 'react';

interface SidebarContextType {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  openSidebarByMouse: () => void;
  closeSidebarByMouse: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const SidebarProvider = ({ children }: { children: ReactNode }) => {
  // Sidebar ouverte par défaut (false = ouvert, true = fermé)
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  const [openedByMouse, setOpenedByMouse] = useState<boolean>(false);

  // Fermer automatiquement la sidebar après 1 seconde au chargement initial
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSidebarCollapsed(true);
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, []); // Effet exécuté uniquement au montage

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
    setOpenedByMouse(false); // Reset mouse flag when manually toggled
  };

  const openSidebarByMouse = () => {
    setSidebarCollapsed(false);
    setOpenedByMouse(true);
  };

  const closeSidebarByMouse = () => {
    if (openedByMouse) {
      setSidebarCollapsed(true);
      setOpenedByMouse(false);
    }
  };

  // Détecter quand la souris touche le bord gauche pour ouvrir le menu
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const handleMouseMove = (e: MouseEvent) => {
      // Si la souris est dans les 3 premiers pixels du bord gauche ET que le menu est fermé
      if (e.clientX <= 3 && sidebarCollapsed) {
        // Petite temporisation pour éviter les déclenchements accidentels
        timeoutId = setTimeout(() => {
          openSidebarByMouse();
        }, 100);
      } else {
        // Annuler le timeout si la souris sort de la zone
        clearTimeout(timeoutId);
      }
    };

    // Ajouter l'event listener seulement si la sidebar est fermée
    if (sidebarCollapsed) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    // Cleanup
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      clearTimeout(timeoutId);
    };
  }, [sidebarCollapsed]);

  return (
    <SidebarContext.Provider value={{
      sidebarCollapsed,
      setSidebarCollapsed,
      toggleSidebar,
      openSidebarByMouse,
      closeSidebarByMouse
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export { SidebarContext }; 